// Subtle astrology-inspired motifs (opacity kept very low).
// Fine constellation lines, thin orbital arcs, sparkle elements — never dominant.

export function Sparkle({ className = "", size = 24 }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 0c.6 6.3 5.7 11.4 12 12-6.3.6-11.4 5.7-12 12-.6-6.3-5.7-11.4-12-12C6.3 11.4 11.4 6.3 12 0z" />
    </svg>
  );
}

export function ConstellationField({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 600 600"
      fill="none"
      aria-hidden="true"
      preserveAspectRatio="xMidYMid slice"
    >
      <g stroke="currentColor" strokeWidth="0.6" opacity="0.9">
        <line x1="60" y1="90" x2="180" y2="140" />
        <line x1="180" y1="140" x2="260" y2="80" />
        <line x1="260" y1="80" x2="380" y2="150" />
        <line x1="380" y1="150" x2="520" y2="110" />
        <line x1="120" y1="300" x2="240" y2="360" />
        <line x1="240" y1="360" x2="330" y2="300" />
        <line x1="330" y1="300" x2="470" y2="380" />
        <line x1="90" y1="500" x2="220" y2="470" />
        <line x1="220" y1="470" x2="340" y2="520" />
        <line x1="340" y1="520" x2="500" y2="470" />
      </g>
      <g fill="currentColor">
        {[
          [60, 90], [180, 140], [260, 80], [380, 150], [520, 110],
          [120, 300], [240, 360], [330, 300], [470, 380],
          [90, 500], [220, 470], [340, 520], [500, 470],
        ].map(([cx, cy], i) => (
          <circle key={i} cx={cx} cy={cy} r={i % 3 === 0 ? 2.4 : 1.6} />
        ))}
      </g>
    </svg>
  );
}

export function OrbitArcs({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 400 400"
      fill="none"
      aria-hidden="true"
    >
      <g stroke="currentColor" strokeWidth="1" opacity="0.9">
        <circle cx="200" cy="200" r="80" />
        <circle cx="200" cy="200" r="130" />
        <circle cx="200" cy="200" r="185" strokeDasharray="3 6" />
      </g>
    </svg>
  );
}

// Vedic Navagraha glyphs — Sun, Moon, Mercury, Venus, Mars, Jupiter, Saturn, Rahu, Ketu.
export const PLANET_GLYPHS = ["\u2609", "\u263D", "\u263F", "\u2640", "\u2642", "\u2643", "\u2644", "\u260A", "\u260B"];

// Subtle zodiac-style ring: concentric circles, 12 radial ticks and 12 dots.
export function CelestialRing({ className = "" }) {
  const ticks = Array.from({ length: 12 });
  const pt = (i, r) => {
    const a = (i / 12) * Math.PI * 2 - Math.PI / 2;
    return [200 + Math.cos(a) * r, 200 + Math.sin(a) * r];
  };
  return (
    <svg className={className} viewBox="0 0 400 400" fill="none" aria-hidden="true">
      <g stroke="currentColor" strokeWidth="1">
        <circle cx="200" cy="200" r="118" opacity="0.5" />
        <circle cx="200" cy="200" r="150" />
        <circle cx="200" cy="200" r="184" strokeDasharray="1.5 9" opacity="0.6" />
      </g>
      <g stroke="currentColor" strokeWidth="1">
        {ticks.map((_, i) => {
          const [x1, y1] = pt(i, 150);
          const [x2, y2] = pt(i, 118);
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
        })}
      </g>
      <g fill="currentColor">
        {ticks.map((_, i) => {
          const [x, y] = pt(i, 166);
          return <circle key={i} cx={x} cy={y} r={i % 3 === 0 ? 3 : 1.8} />;
        })}
      </g>
    </svg>
  );
}

// A slim horizontal row of planetary glyphs, used as a celestial divider/accent.
export function CelestialGlyphRow({ className = "" }) {
  return (
    <div className={`flex items-center justify-center gap-4 sm:gap-6 ${className}`} aria-hidden="true">
      {PLANET_GLYPHS.map((g, i) => (
        <span key={i} className="font-heading text-lg leading-none sm:text-xl">{g}</span>
      ))}
    </div>
  );
}
